import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ImportForm from '../../forms/ImportForm';
import PageHeader from '../../components/page/PageHeader';
import DocumentHead from '../../components/DocumentHead';

import { useCreatePlayerMutation } from '../../api/teams';

import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';

const STATUS_SUCCESS = 'success';
const STATUS_ERROR = 'error';
const STATUS_LOADING = 'loading';

const columns = [
    {
        field: 'firstName',
        header: 'First Name',
    },
    {
        field: 'lastName',
        header: 'Last Name',
    },
    {
        field: 'displayName',
        header: 'Display Name',
    },
    {
        field: 'uniformNumber',
        header: 'Playing Number',
    },
    {
        field: 'dateOfBirth',
        header: 'Date Of Birth',
    },
    {
        field: 'gender',
        header: 'Gender',
    },
    {
        field: 'colour',
        header: 'Colour',
    },
    {
        field: 'startDate',
        header: 'Start Date',
    },
    {
        field: 'endDate',
        header: 'End Date',
    },
];

const PlayerImport = (props) => {
    const [importedData, setImportedData] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toast = useRef(null);

    const { teamID } = useParams();

    const navigate = useNavigate();

    // API Handling
    const [createPlayer] = useCreatePlayerMutation({ teamID });

    const handleSubmit = async () => {
        let successCount = 0;

        setIsSubmitting(true);

        await Promise.allSettled(
            importedData.map(async (data, rowIndex) => {
                updateRowStatus(rowIndex, STATUS_LOADING);

                const response = await createPlayer({
                    teamID,
                    ...data,
                    status: 'Active',
                });

                if (response.data) {
                    successCount++;
                    updateRowStatus(rowIndex, STATUS_SUCCESS);
                } else {
                    updateRowStatus(rowIndex, STATUS_ERROR);
                }
            })
        ).then(() => {
            setIsSubmitting(false);
        });

        toast.current.show({
            severity:
                successCount === 0
                    ? 'error'
                    : successCount === importedData.length
                        ? 'success'
                        : '',
            className:
                successCount === 0 || successCount === importedData.length
                    ? 'p-toast-message--is-inline'
                    : '',
            summary: `${successCount}/${importedData.length} Imported`,
            detail: successDetailContent(successCount, importedData.length),
            life: 5000,
        });
    };

    const successDetailContent = (count, maxCount) => {
        return (
            <>
                {maxCount === count ? (
                    <Button
                        className="p-button-outlined p-button-sm p-button-secondary toast-action"
                        label="View"
                        onClick={() =>
                            navigate('/', {
                                replace: true,
                            })
                        }
                    />
                ) : count > 0 ? (
                    <div className="button-group">
                        <Button
                            className="p-button-outlined p-button-sm p-button-warn toast-action"
                            label="Clear And Retry"
                            onClick={() => handleRetryImport(count)}
                        />
                        <Button
                            className="p-button-outlined p-button-sm p-button-secondary toast-action"
                            label="View Imported"
                            onClick={() =>
                                navigate('/', {
                                    replace: true,
                                })
                            }
                        />
                    </div>
                ) : (
                    <Button
                        className="p-button-outlined p-button-sm p-button-secondary toast-action"
                        label="Retry"
                        onClick={() => handleSubmit()}
                    />
                )}
            </>
        );
    };

    const updateRowStatus = (rowIndex, status) => {
        setImportedData((prevData) =>
            prevData.map((item, index) =>
                index === rowIndex ? { ...item, status } : item
            )
        );
    };

    const handleImport = (data) => {
        setImportedData(data);
    };

    const handleClearAll = () => {
        setImportedData([]);
    };

    const handleClearCompleted = () => {
        setImportedData((prevState) =>
            prevState.filter((data) => data.status !== STATUS_SUCCESS)
        );
    };

    const handleRetryImport = (completedCount) => {
        if (completedCount > 0) {
            handleClearCompleted();
        }
        handleSubmit();
    };

    const handleFileError = () => {
        toast.current.show({
            severity: 'error',
            summary: `File type not supported`,
            detail: 'Import a CSV or Excel Sheet',
            life: 5000,
        });
    };

    const handleRemoveRow = (index) => {
        setImportedData((data) => [
            ...data.slice(0, index),
            ...data.slice(index + 1),
        ]);
    };

    const handleAddRow = (data) => {
        setImportedData(data);
    };

    const handleEditComplete = (data) => {
        setImportedData(data);
    };

    return (
        <>
            <DocumentHead
                title="Player Import - Rookie Me Hub"
                description="Player import page"
            />
            <div className="page view">
                <div className="view-inner">
                    <PageHeader title='Players' showBreadcrumbs={false} />
                    <ImportForm
                        columns={columns}
                        data={importedData}
                        isSubmitting={isSubmitting}
                        onAddRow={handleAddRow}
                        onClearAll={handleClearAll}
                        onClearCompleted={handleClearCompleted}
                        onImportError={handleFileError}
                        onEditComplete={handleEditComplete}
                        onRemoveRow={handleRemoveRow}
                        onSubmit={handleSubmit}
                        onImportComplete={handleImport}
                    />
                    <Toast ref={toast} />
                </div>
            </div>
        </>
    );
};

export default PlayerImport;
